<script setup lang="ts">

import MediumModel from '@/components/Interface/Modal/ModelSizes/MediumModel.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import {useAssetStore} from "@/stores/assetStore.ts";
import {computed} from "vue";
import { CDN } from '@/models/cdn.ts'
import BuildingEffects from '@/components/Sections/Planets/PlanetTabs/Generic/BuildingEffects.vue'
import MetricDisplay from '@/components/Elements/Generic/MetricDisplay.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import StyledImage from '@/components/Elements/Images/StyledImage.vue'
import { formatShort } from '@/models/formatNumber.ts'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const gameStore = useGameStore()
const assetStore = useAssetStore()

const buildingData = computed(() => assetStore.findBuildingByCode(gameStore.modalData.buildingCode)!)

const productionObject = computed(() => assetStore.findProjectObjectByBuildingCode(buildingData.value.buildingCode)!)

const canPurchaseWithOre = computed(() => {
  if (!buildingData.value) return false
  return !!buildingData.value.purchaseWithOre &&
         !!buildingData.value.purchaseWithOre.oreCode &&
         buildingData.value.purchaseWithOre.quantity > 0
})

const oreData = computed(() => {
  if (!canPurchaseWithOre.value || !buildingData.value.purchaseWithOre) return null

  const oreCode = buildingData.value.purchaseWithOre.oreCode
  const allOres = Object.values(assetStore.ores)
  const ore = allOres.find(o => o.code === oreCode)

  return ore || null
})

</script>

<template>

  <MediumModel :title="t(`buildings.buildings.${buildingData.buildingCode}.name`)">
    <div class="p-3 md:p-3">
      <div class="flex flex-col sm:flex-row text-center sm:text-left">
        <StyledImage
          :src="CDN.building(256, buildingData.buildingCode)"
          size="lg"
          class="shrink-0"
        />
        <div class="flex-grow text-subtext lg:pl-3 pl-0 pt-2 sm:pt-0">
          <p class="pb-4">{{ t(`buildings.buildings.${buildingData.buildingCode}.longDescription`) }}</p>
          <div class="flex flex-wrap gap-4 pb-4">
            <MetricDisplay :text="t('modals.building.productionCost')">
              <GenericIconWithNumber :amount="productionObject.productionCost" type="production"/>
            </MetricDisplay>
            <MetricDisplay v-if="canPurchaseWithOre && oreData" :text="t('modals.building.instantPurchaseCost')">
              <div class="flex items-center">
                <img
                  :src="CDN.ore(oreData.id, 64)"
                  class="inline"
                  style="width: 20px; height: 20px;"
                  :alt="oreData.oreName"
                />
                <span class="text-metal-300 ps-2">{{ formatShort(buildingData.purchaseWithOre!.quantity) }} {{ oreData.oreName }}</span>
              </div>
            </MetricDisplay>
            <MetricDisplay :text="t('modals.building.effects')">
              <BuildingEffects :building="buildingData"> </BuildingEffects>
            </MetricDisplay>
          </div>
        </div>
      </div>
    </div>
  </MediumModel>

</template>

<style scoped lang="scss">
</style>